import { useState, useEffect, useMemo } from 'react';
import { api, ApiError } from '../lib/api';
import {
  AdvertiserResponse,
  campaignStyleEnum,
  SuppressionFileListRowResponse,
} from '@leads-platform/shared';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { SearchInput } from '../components/ui/search-input';
import { Select } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Typeahead } from '../components/typeahead';
import { useToast } from '../components/ui/toast';
import { campaignStyleEnumToLabel } from '../lib/campaign-styles';

/** Campaign Type filter options: All + the four styles (task 069-02, D3). */
const CAMPAIGN_TYPE_OPTIONS = campaignStyleEnum.options.map((style) => ({
  value: style,
  label: campaignStyleEnumToLabel(style),
}));

export function Suppression() {
  const [files, setFiles] = useState<SuppressionFileListRowResponse[]>([]);
  const [advertisers, setAdvertisers] = useState<AdvertiserResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [campaignType, setCampaignType] = useState('');
  const [advertiserId, setAdvertiserId] = useState('');
  const [filenameQuery, setFilenameQuery] = useState('');
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const { success, error: toastError } = useToast();

  const loadFiles = () => {
    api
      .getSuppressionFileList()
      .then((filesData) => {
        setFiles(filesData);
      })
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(() => {
    loadFiles();
    api
      .getAdvertisers()
      .then((advertisersData) => {
        setAdvertisers(advertisersData);
      })
      .catch((err: ApiError) => setError(err.message));
  }, []);

  // Client-side filtering over the listing (D3's rejection of server-side
  // search — row counts don't warrant it). Filters combine with AND.
  const filteredFiles = useMemo(() => {
    const needle = filenameQuery.trim().toLowerCase();
    return files.filter((file) => {
      if (advertiserId && file.advertiserId !== advertiserId) return false;
      if (campaignType && !file.attachedCampaigns.some((c) => c.campaignStyle === campaignType)) {
        return false;
      }
      if (needle && !file.originalFilename.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [files, campaignType, advertiserId, filenameQuery]);

  /**
   * Delete stays per-campaign (D3: unchanged controller): the file is
   * detached through each attached campaign's route, and the api removes the
   * row (and its disk file) once the last reference goes. A file with no
   * attached campaign has no route to go through — the per-campaign flow that
   * owns uploads/deletes is the only surface for it — so its row carries no
   * Delete button.
   */
  const handleDelete = async (file: SuppressionFileListRowResponse) => {
    if (file.attachedCampaigns.length === 0) {
      return;
    }

    // Confirm before deleting
    if (!confirm('Delete this suppression file?')) {
      return;
    }

    setDeletingFileId(file.id);
    try {
      for (const campaign of file.attachedCampaigns) {
        await api.deleteSuppressionFile(campaign.id, file.id);
      }
      success('Suppression file deleted successfully');
      loadFiles();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to delete suppression file');
    } finally {
      setDeletingFileId(null);
    }
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading suppression files: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Suppression Files</h1>
        <p className="text-muted-foreground mt-2">Manage suppression files for your campaigns</p>
        <p className="text-sm text-muted-foreground mt-1">
          Upload suppression files from the campaign&apos;s Suppression section (saved with
          Update/Create).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Uploaded Files</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Select
              aria-label="Campaign Type"
              value={campaignType}
              onChange={(e) => setCampaignType(e.target.value)}
              className="h-9 min-w-[160px]"
            >
              <option value="">All types</option>
              {CAMPAIGN_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Typeahead<AdvertiserResponse>
              aria-label="Advertiser"
              options={advertisers}
              value={advertiserId || null}
              onChange={(v) => setAdvertiserId(v ?? '')}
              getId={(a) => a.id}
              getLabel={(a) => a.name}
              allOption={{ label: 'All advertisers' }}
              className="min-w-[170px]"
            />

            <SearchInput
              value={filenameQuery}
              onChange={setFilenameQuery}
              placeholder="Search filename"
              className="min-w-[220px]"
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Filename</TableHead>
                <TableHead>Advertiser</TableHead>
                <TableHead>Attached Campaigns</TableHead>
                <TableHead>Row Count</TableHead>
                <TableHead>Hash Algorithm</TableHead>
                <TableHead>Uploaded By</TableHead>
                <TableHead>Uploaded At</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No suppression files uploaded yet.
                  </TableCell>
                </TableRow>
              ) : filteredFiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No suppression files match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredFiles.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell className="font-medium">{file.originalFilename}</TableCell>
                    <TableCell>{file.advertiserName}</TableCell>
                    <TableCell>
                      {file.attachedCampaigns.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        file.attachedCampaigns
                          .map((c) => `${c.name} (${campaignStyleEnumToLabel(c.campaignStyle)})`)
                          .join(', ')
                      )}
                    </TableCell>
                    <TableCell>{file.rowCount ?? 'Unknown'}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {file.hashAlgorithm}
                      </span>
                    </TableCell>
                    <TableCell>{file.uploadedBy}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(file.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {file.attachedCampaigns.length > 0 && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(file)}
                          disabled={deletingFileId === file.id}
                        >
                          {deletingFileId === file.id ? 'Deleting...' : 'Delete'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
